import { useEffect, useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { crmService } from '../services/crmService';
import { CRM_QUERY_KEYS } from './useClients';
import {
  buildClientGeocodeQuery,
  hasGeocodableAddress,
  mergeClientWithDetails,
} from '../utils/clientAddress';
import { geocodeClientsBatch, resolveCachedClientLocations } from '../utils/geocodeService';

const DETAILS_BATCH_SIZE = 12;

const enrichClientsFromCache = (clients, queryClient) =>
  clients.map((client) => {
    const cachedDetails = queryClient.getQueryData(CRM_QUERY_KEYS.clientDetails(client.id));
    return mergeClientWithDetails(client, cachedDetails);
  });

const fetchMissingClientDetails = async (clients, queryClient, attemptedIds) => {
  const missing = clients.filter((client) => {
    if (hasGeocodableAddress(client)) return false;
    if (attemptedIds.has(client.id)) return false;
    return !queryClient.getQueryData(CRM_QUERY_KEYS.clientDetails(client.id));
  });

  if (!missing.length) return clients;

  const enriched = [...clients];

  for (let i = 0; i < missing.length; i += DETAILS_BATCH_SIZE) {
    const batch = missing.slice(i, i + DETAILS_BATCH_SIZE);
    batch.forEach((client) => attemptedIds.add(client.id));

    const results = await Promise.all(
      batch.map(async (client) => {
        try {
          const details = await crmService.getClientDetails(client.id);
          queryClient.setQueryData(CRM_QUERY_KEYS.clientDetails(client.id), details);
          return mergeClientWithDetails(client, details);
        } catch {
          return client;
        }
      })
    );

    results.forEach((updated) => {
      const index = enriched.findIndex((item) => item.id === updated.id);
      if (index >= 0) enriched[index] = updated;
    });
  }

  return enriched;
};

const buildFailedClientsList = (clients, failedIdsSet) =>
  clients.reduce((failedList, client) => {
    const query = buildClientGeocodeQuery(client);
    if (query && !failedIdsSet.has(client.id)) {
      return failedList;
    }

    const partialAddress = [client.address, client.postalCode, client.city]
      .filter(Boolean)
      .join(', ');

    failedList.push({
      id: client.id,
      companyName: client.companyName || '—',
      address: query || partialAddress || 'Brak danych adresowych',
      reason: query ? 'not_found' : 'missing',
    });

    return failedList;
  }, []);

const applyResolvedState = ({
  clients,
  resolved,
  setLocations,
  setFailedClients,
  setFailedCount,
  setProgress,
}) => {
  setLocations(resolved.locations);
  const failedIdsSet = new Set(resolved.failedIds);
  setFailedClients(buildFailedClientsList(clients, failedIdsSet));
  setFailedCount(failedIdsSet.size);
  setProgress({
    done: resolved.locations.length + resolved.failedIds.length,
    total: clients.filter((client) => client.geocodeQuery).length,
  });
};

export const useMapClientLocations = (clients, enabled = true) => {
  const queryClient = useQueryClient();
  const [geocodeError, setGeocodeError] = useState(null);
  const [locations, setLocations] = useState([]);
  const [failedCount, setFailedCount] = useState(0);
  const [failedClients, setFailedClients] = useState([]);
  const [isGeocoding, setIsGeocoding] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const runIdRef = useRef(0);
  const lastResolvedKeyRef = useRef('');
  const detailsFetchAttemptedRef = useRef(new Set());

  const preparedClients = useMemo(() => {
    if (!clients?.length) return [];
    return enrichClientsFromCache(clients, queryClient).map((client) => ({
      ...client,
      geocodeQuery: buildClientGeocodeQuery(client),
    }));
  }, [clients, queryClient]);

  const geocodeRequestKey = useMemo(
    () => preparedClients.map((client) => `${client.id}:${client.geocodeQuery}:${client.status}`).join('|'),
    [preparedClients]
  );

  useEffect(() => {
    if (!enabled || !preparedClients.length) {
      setLocations([]);
      setFailedCount(0);
      setFailedClients([]);
      setIsGeocoding(false);
      setGeocodeError(null);
      lastResolvedKeyRef.current = '';
      return undefined;
    }

    if (geocodeRequestKey === lastResolvedKeyRef.current) {
      return undefined;
    }

    const runId = runIdRef.current + 1;
    runIdRef.current = runId;
    let cancelled = false;

    const run = async () => {
      const initialResolved = resolveCachedClientLocations(preparedClients);

      if (!cancelled && runIdRef.current === runId) {
        applyResolvedState({
          clients: preparedClients,
          resolved: initialResolved,
          setLocations,
          setFailedClients,
          setFailedCount,
          setProgress,
        });
      }

      const needsDetailsFetch = preparedClients.some((client) => !client.geocodeQuery);
      const needsNetwork = initialResolved.pending.length > 0 || needsDetailsFetch;

      if (!needsNetwork) {
        if (!cancelled && runIdRef.current === runId) {
          setIsGeocoding(false);
          setGeocodeError(null);
          lastResolvedKeyRef.current = geocodeRequestKey;
        }
        return;
      }

      setIsGeocoding(true);
      setGeocodeError(null);

      try {
        const withDetails = needsDetailsFetch
          ? await fetchMissingClientDetails(
            preparedClients,
            queryClient,
            detailsFetchAttemptedRef.current
          )
          : preparedClients;

        if (cancelled || runIdRef.current !== runId) return;

        const geocodeInput = withDetails
          .map((client) => ({
            ...client,
            geocodeQuery: buildClientGeocodeQuery(client),
          }))
          .filter((client) => client.geocodeQuery);

        const resolvedAfterDetails = resolveCachedClientLocations(withDetails);

        if (!cancelled && runIdRef.current === runId) {
          applyResolvedState({
            clients: withDetails,
            resolved: resolvedAfterDetails,
            setLocations,
            setFailedClients,
            setFailedCount,
            setProgress,
          });
        }

        if (!resolvedAfterDetails.pending.length) {
          lastResolvedKeyRef.current = geocodeRequestKey;
          return;
        }

        const { locations: geocoded, failedIds } = await geocodeClientsBatch(resolvedAfterDetails.pending, {
          onProgress: (done, total) => {
            if (!cancelled && runIdRef.current === runId) {
              setProgress({ done, total });
            }
          },
        });

        if (cancelled || runIdRef.current !== runId) return;

        const mergedLocations = [...resolvedAfterDetails.locations, ...geocoded];
        const failedIdsSet = new Set([...resolvedAfterDetails.failedIds, ...failedIds]);

        setLocations(mergedLocations);
        setFailedClients(buildFailedClientsList(withDetails, failedIdsSet));
        setFailedCount(failedIdsSet.size);
        lastResolvedKeyRef.current = geocodeRequestKey;
      } catch (error) {
        if (!cancelled && runIdRef.current === runId) {
          setGeocodeError(error.message || 'Błąd geokodowania adresów');
        }
      } finally {
        if (!cancelled && runIdRef.current === runId) {
          setIsGeocoding(false);
        }
      }
    };

    run();

    return () => {
      cancelled = true;
    };
  }, [enabled, geocodeRequestKey, preparedClients, queryClient]);

  return {
    geocodeError,
    locations,
    failedCount,
    failedClients,
    isGeocoding,
    progress,
    totalClients: preparedClients.length,
  };
};
